import { useCallback, useEffect, useRef, useState } from 'react';

import {
  findNextWordIndex,
  findFirstDiffIndex,
} from '../utils/text-animation-utils.js';

export const ANIMATION_DURATION_MS = 200;
const STAGGER_DELAY_MS = 5;

/**
 * Hook to animate a text word by word.
 * @param text - the full text content to be animated.
 * @param reduceMotion - whether to reduce motion and disable animations.
 * @param responseState - the current state of the response ('static', 'streaming', 'complete').
 * @param onAnimationStateChange - optional callback fired when animation starts or finishes
 *   (including the CSS fade-in settling time of the last word).
 * @returns the portion of text that can already be rendered and a boolean indicating whether the text is still animating.
 * @internal
 */
export const useTextAnimation = (
  text: string,
  reduceMotion: boolean,
  responseState: 'static' | 'streaming' | 'complete',
  onAnimationStateChange?: (isAnimating: boolean) => void,
): { displayedText: string; isTextAnimating: boolean } => {
  const [displayedText, setDisplayedText] = useState(reduceMotion ? text : '');
  const [isTextAnimating, setIsTextAnimating] = useState(false);

  const displayedTextRef = useRef(displayedText);
  const cursorRef = useRef(0);
  const wordTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const animationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTextAnimatingRef = useRef(false);

  // Updates both state and ref for the displayed text
  const updateDisplayedText = useCallback((newText: string) => {
    setDisplayedText(newText);
    displayedTextRef.current = newText;
  }, []);

  // Clear any existing word-scheduling timer
  const clearWordTimer = useCallback(() => {
    if (wordTimerRef.current) {
      clearTimeout(wordTimerRef.current);
      wordTimerRef.current = null;
    }
  }, []);

  // Clear any existing animation timer
  const clearAnimationTimer = useCallback(() => {
    if (animationTimerRef.current) {
      clearTimeout(animationTimerRef.current);
      animationTimerRef.current = null;
    }
  }, []);

  const stopAnimating = useCallback(() => {
    if (isTextAnimatingRef.current) {
      setIsTextAnimating(false);
      isTextAnimatingRef.current = false;
      onAnimationStateChange?.(false);
    }
  }, [onAnimationStateChange]);

  const startAnimating = useCallback(() => {
    if (!isTextAnimatingRef.current) {
      setIsTextAnimating(true);
      isTextAnimatingRef.current = true;
      onAnimationStateChange?.(true);
    }
  }, [onAnimationStateChange]);

  // Wait for the CSS animation of the last word to finish before stopping the animation
  const scheduleAnimationEnd = useCallback(() => {
    animationTimerRef.current = setTimeout(() => {
      animationTimerRef.current = null;
      stopAnimating();
    }, ANIMATION_DURATION_MS);
  }, [stopAnimating]);

  // Animate word by word
  const scheduleNextWord = useCallback(
    (fullText: string) => {
      if (wordTimerRef.current || cursorRef.current >= fullText.length) {
        return;
      }

      // Cancel any pending animation timer and mark as animating
      clearAnimationTimer();
      startAnimating();

      wordTimerRef.current = setTimeout(() => {
        wordTimerRef.current = null;

        let nextCursor = findNextWordIndex(fullText, cursorRef.current);

        // Ensure progress to avoid infinite loop
        if (nextCursor === cursorRef.current) {
          nextCursor = Math.min(cursorRef.current + 1, fullText.length);
        }

        cursorRef.current = nextCursor;
        updateDisplayedText(fullText.slice(0, nextCursor));

        if (cursorRef.current < fullText.length) {
          scheduleNextWord(fullText);
        } else {
          // All words rendered — wait for the CSS animation to finish
          scheduleAnimationEnd();
        }
      }, STAGGER_DELAY_MS);
    },
    [
      clearAnimationTimer,
      startAnimating,
      updateDisplayedText,
      scheduleAnimationEnd,
    ],
  );

  // Word-by-word text rendering
  useEffect(() => {
    // If animation is disabled, show full text immediately
    if (reduceMotion || responseState === 'static') {
      clearWordTimer();
      clearAnimationTimer();
      cursorRef.current = text.length;
      updateDisplayedText(text);
      stopAnimating();
      return clearWordTimer;
    }

    // Handle text reset
    if (text.length === 0) {
      clearWordTimer();
      clearAnimationTimer();
      cursorRef.current = 0;
      updateDisplayedText('');
      stopAnimating();
      return clearWordTimer;
    }

    // Find where the new text differs from the displayed text
    const sharedPrefixIndex = findFirstDiffIndex(
      displayedTextRef.current,
      text,
    );

    // Reset to shared prefix when the text changes
    if (sharedPrefixIndex !== displayedTextRef.current.length) {
      updateDisplayedText(text.slice(0, sharedPrefixIndex));
      cursorRef.current = sharedPrefixIndex;
    }

    // If already at the end, clear the timer and render the full text
    if (cursorRef.current >= text.length) {
      clearWordTimer();
      updateDisplayedText(text);
      return clearWordTimer;
    }

    // New words to render
    scheduleNextWord(text);

    return () => {
      clearWordTimer();
      clearAnimationTimer();
    };
  }, [
    responseState,
    reduceMotion,
    text,
    clearWordTimer,
    clearAnimationTimer,
    updateDisplayedText,
    stopAnimating,
    scheduleNextWord,
  ]);

  // when responseState changes to 'complete' after `isTextAnimating` was already set to false
  // we need to ensure that `isTextAnimating` has the correct value
  const isFullTextRendered = displayedText === text;

  return {
    displayedText,
    isTextAnimating: !isFullTextRendered || isTextAnimating,
  };
};
